import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Col, Row, Spinner, Table, Badge } from 'react-bootstrap';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { showError, showSuccess } from '../../../../core/alert/sweetAlertConfig';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';

export interface KeyDTO {
  id: string;
  keyCode: string;
  doorId: string | null;
  roomId: string | null;
  roomName: string | null;
  floorId: string | null;
  floorName: string | null;
  buildingId: string | null;
  buildingName: string | null;
  vehicleId: string | null;
  vehicleNumberPlate: string | null;
  noOfKeys: number;
  separatedKeys: number | 0;
  bundles: { count: number }[] | 0;
  responsibleParty: string | null;
  isRestricted: boolean;
  handoverStatus: string | null;
  status: string;
  createdBy: string;
  createdAt: string;
  updatedBy: string | null;
  updatedAt: string | null;
}

interface UpdateKeysModuleProps {
  show: boolean;
  onHide: () => void;
  keys: KeyDTO[];
  doorId: number;
  roomId: number;
  onKeysUpdated: () => void;
}

const statusVariant = (s: string) => {
  switch (s) {
    case 'ACTIVE':
      return 'success';
    case 'INACTIVE':
      return 'secondary';
    default:
      return 'info';
  }
};

const UpdateKeysModule: React.FC<UpdateKeysModuleProps> = ({ show, onHide, keys, doorId, roomId, onKeysUpdated }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState<{
    separatedKeys: number;
    bundles: { count: number }[];
    responsibleParty: string;
    isRestricted: boolean;
    handoverStatus: string;
    status: string;
  }>({
    separatedKeys: 0,
    bundles: [{ count: 1 }],
    responsibleParty: '',
    isRestricted: false,
    handoverStatus: 'PENDING',
    status: 'ACTIVE',
  });

  // Calculate total keys
  const totalKeys = formData.separatedKeys + formData.bundles.reduce((sum, bundle) => sum + (bundle.count || 0), 0);

  // Store original total keys for comparison
  const originalTotalKeys = keys.reduce((sum, key) => sum + key.noOfKeys, 0);

  useEffect(() => {
    if (show && keys.length > 0) {
      const firstKey = keys[0];
      const separatedKeys = firstKey?.separatedKeys != null ? Number(firstKey.separatedKeys) : 0;
      const bundles = Array.isArray(firstKey?.bundles) && firstKey.bundles.length > 0
        ? firstKey.bundles.map(bundle => ({ count: Number(bundle.count) }))
        : [{ count: 1 }];
      setFormData({
        separatedKeys,
        bundles,
        responsibleParty: firstKey?.responsibleParty || '',
        isRestricted: firstKey?.isRestricted || false,
        handoverStatus: firstKey?.handoverStatus || 'PENDING',
        status: firstKey?.status || 'ACTIVE',
      });
    }
  }, [show, keys]);

  const handleAddBundle = () => {
    setFormData({
      ...formData,
      bundles: [...formData.bundles, { count: 1 }],
    });
  };

  const handleRemoveBundle = (index: number) => {
    setFormData({
      ...formData,
      bundles: formData.bundles.filter((_, i) => i !== index),
    });
  };

  const handleBundleCountChange = (index: number, value: number) => {
    const updatedBundles = [...formData.bundles];
    updatedBundles[index] = { count: Math.max(1, value) };
    setFormData({ ...formData, bundles: updatedBundles });
  };

  const handleUpdateKeys = async () => {
    if (!checkPermission('ROLE_UPDATE_KEY')) {
      showError('You do not have permission to update keys.');
      return;
    }
    if (totalKeys <= 0) {
      showError('Total keys must be greater than 0.');
      return;
    }
    if (formData.bundles.some(bundle => bundle.count <= 0)) {
      showError('Each bundle must have at least 1 key.');
      return;
    }
    setIsLoading(true);
    try {
      const updatePromises = keys.map(async (key) => {
        const payload = {
          id: key.id,
          doorId: key.doorId,
          roomId: key.roomId,
          vehicleId: key.vehicleId,
          noOfKeys: totalKeys,
          separatedKeys: formData.separatedKeys || 0,
          bundles: formData.bundles.map(bundle => ({ count: bundle.count })) || null,
          responsibleParty: formData.responsibleParty || null,
          isRestricted: formData.isRestricted,
          handoverStatus: formData.handoverStatus || null,
          status: formData.status,
        };
        return apiDefinitions.updateKey(payload);
      });
      const results = await Promise.all(updatePromises);
      const allSuccess = results.every(res => res.status === 200 && res.data.status === 'S1000');
      if (allSuccess) {
        showSuccess("Keys updated successfully");
        onKeysUpdated();
        onHide();
      } else {
        const errorMessages = results
          .filter(res => res.status !== 200 || res.data.status !== 'S1000')
          .map(res => res.data?.statusDescription || 'Failed to update key');
        showError(`Failed to update some keys: ${errorMessages.join(', ')}`);
      }
    } catch (e: any) {
      showError(`Failed to update keys: ${e.response?.data?.statusDescription || e.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered size="xl">
      <Modal.Header closeButton>
        <Modal.Title>Update All Keys</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {isLoading ? (
          <div className="text-center">
            <Spinner animation="border" role="status">
              <span className="visually-hidden">Loading...</span>
            </Spinner>
          </div>
        ) : (
          <>
            <Form>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group controlId="noOfKeys">
                    <Form.Label>Total Keys</Form.Label>
                    <Form.Control type="number" min="0" value={totalKeys} readOnly />
                    <small className="text-muted">Auto-calculated as Separated + sum(Bundle counts)</small>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="separatedKeys">
                    <Form.Label>Separated Keys</Form.Label>
                    <Form.Control
                      type="number"
                      min="0"
                      value={formData.separatedKeys}
                      onChange={(e) => setFormData({ ...formData, separatedKeys: parseInt(e.target.value) || 0 })}
                    />
                  </Form.Group>
                </Col>
                <Col md={6}>
                  <Form.Label>Bundles</Form.Label>
                  {formData.bundles.map((bundle, index) => (
                    <Row key={index} className="mb-2 align-items-center">
                      <Col xs={8}>
                        <Form.Group controlId={`bundle-${index}`}>
                          <Form.Control
                            type="number"
                            min="1"
                            placeholder={`Keys in bundle ${index + 1}`}
                            value={bundle.count}
                            onChange={(e) => handleBundleCountChange(index, parseInt(e.target.value) || 1)}
                          />
                        </Form.Group>
                      </Col>
                      <Col xs={4}>
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveBundle(index)}
                          disabled={formData.bundles.length === 0}
                        >
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  ))}
                  <Button variant="outline-primary" size="sm" onClick={handleAddBundle} className="mt-2">
                    Add Bundle
                  </Button>
                </Col>
              </Row>
              <Row className="mb-3">
                <Col md={3}>
                  <Form.Group controlId="responsibleParty">
                    <Form.Label>Responsible Party</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.responsibleParty}
                      onChange={(e) => setFormData({ ...formData, responsibleParty: e.target.value })}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="handoverStatus">
                    <Form.Label>Handover Status</Form.Label>
                    <Form.Control
                      as="select"
                      value={formData.handoverStatus}
                      onChange={(e) => setFormData({ ...formData, handoverStatus: e.target.value })}
                    >
                      <option value="PENDING">PENDING</option>
                      <option value="YES">YES</option>
                      <option value="NO">NO</option>
                      <option value="">Not Set</option>
                    </Form.Control>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="status">
                    <Form.Label>Status</Form.Label>
                    <Form.Select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="INACTIVE">INACTIVE</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group controlId="isRestricted">
                    <Form.Check
                      type="checkbox"
                      label="Restricted"
                      checked={formData.isRestricted}
                      onChange={(e) => setFormData({ ...formData, isRestricted: e.target.checked })}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Form>
            <Table striped bordered hover responsive className="mt-4">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Key Code</th>
                  <th>Keys</th>
                  <th>Responsible Party</th>
                  <th>Restricted</th>
                  <th>H_Status</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {keys.map((key) => (
                  <tr key={key.id}>
                    <td>{key.id}</td>
                    <td>{key.keyCode}</td>
                    <td>{key.noOfKeys}</td>
                    <td>{key.responsibleParty ?? '-'}</td>
                    <td>{key.isRestricted ? 'Yes' : 'No'}</td>
                    <td>{key.handoverStatus ?? '-'}</td>
                    <td>
                      <Badge bg={statusVariant(key.status)}>{key.status}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            <div className="d-flex justify-content-end mt-3">
              <Button
                variant="primary"
                className="me-2"
                onClick={onHide}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                variant="success"
                onClick={handleUpdateKeys}
                disabled={isLoading}
              >
                {isLoading ? <Spinner size="sm" /> : 'Update All Keys'}
              </Button>
            </div>
          </>
        )}
      </Modal.Body>
    </Modal>
  );
};

export default UpdateKeysModule;